import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react'
import { useOmniAuth } from '@omni/auth'
import { Button } from '@omni/ui'
import { MailCheck } from 'lucide-react'
import { instance, productName, shellProject } from '../lib/config'
import { rememberReturnTo } from '../lib/return-to'
import { clearSignInNotice, peekSignInNotice, providerErrorMessage } from '../lib/signin-notice'
import { LoginRoom } from './LoginRoom'

type Status = 'idle' | 'sending' | 'sent' | 'verifying' | 'error'

// The entry screen (Sam, 9 Oct 2026: every Command Centre's sign-in must be
// impressive; Gulf Western's /login is the standard). The customer's brand
// ground, their real logo (never redrawn), the "<Name> / COMMAND CENTRE /
// <tagline>" lockup, then a white card with the sign-in form. Branding comes
// from instance.entry, falling back to the theme, so every instance gets it.
//
// One email for the whole command centre. It carries BOTH a link and a
// 6-digit code. The code is typed into THIS page, so the session always
// lands in the browser the user is actually looking at (links can open in a
// different context: mail-app in-app browsers, scanners, other devices).

/** Mix a #rrggbb colour toward white (amount 0..1). Anything else passes through. */
function tint(hex: string, amount: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex)
  if (!m) return hex
  const n = parseInt(m[1], 16)
  const mix = (c: number) => Math.round(c + (255 - c) * amount)
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(mix)
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

/** #rrggbb + alpha → rgba(). Anything else passes through. */
function alpha(hex: string, a: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex)
  if (!m) return hex
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}

const theme = instance.theme
const entry = instance.entry ?? {}
const GROUND = entry.background ?? theme.gradient ?? 'linear-gradient(160deg, #0b0c18 0%, #15162b 100%)'
const HIGHLIGHT = entry.highlight ?? tint(theme.accent, 0.45)
// The ambient glow takes the brand accent (the highlight can be a yellow or
// gold that turns muddy when blurred over a dark ground).
const GLOW = entry.glow ?? alpha(tint(theme.accent, 0.15), 0.3)
const LOGO = entry.logoUrl ?? theme.wordmarkUrl ?? theme.logoUrl
const LOGO_HEIGHT = entry.logoHeight ?? 72
const COVER = entry.cover
// ONA spells it "Center"; everyone else "Centre". Follow the product name.
// A product named "<Display name> <Something else>" ("Nateva Intelligence")
// shows its own name instead: the remainder under the kicker, the full name
// in the copy. Every other tenant keeps the Command Centre wording.
const OWN_NAME =
  !!instance.copy?.productName &&
  !/command cent(er|re)/i.test(productName) &&
  productName.toLowerCase().startsWith(`${instance.displayName.toLowerCase()} `)
// A front-door build (__FRONT_DOOR__, build-time) always uses its own name, so it never carries the words.
const TITLE = __FRONT_DOOR__ || OWN_NAME
  ? productName.slice(instance.displayName.length + 1)
  : /command center/i.test(productName) ? 'Command Center' : 'Command Centre'
const OPEN_NAME = __FRONT_DOOR__ || OWN_NAME ? productName : `the ${instance.displayName} ${TITLE}`
const ADMIN_NAME = __FRONT_DOOR__ || OWN_NAME ? productName : TITLE

// Microsoft sign-in (customer front door, section 5): only for an instance
// that lists 'azure' in frontDoor.providers, and only once the provider is
// actually switched on in its Supabase project (read from the public
// /auth/v1/settings). Until the customer's IT registers the app, the entry
// screen looks exactly as it does today. Every other instance never asks.
const OFFERS_MICROSOFT = instance.frontDoor?.providers?.includes('azure') ?? false

function useProviderLive(provider: 'azure', offered: boolean): boolean {
  const [live, setLive] = useState(false)
  useEffect(() => {
    if (!offered) return
    let cancelled = false
    fetch(`${shellProject.url}/auth/v1/settings`, { headers: { apikey: shellProject.anonKey } })
      .then((r) => (r.ok ? r.json() : null))
      .then((s: { external?: Record<string, boolean> } | null) => {
        if (!cancelled) setLive(s?.external?.[provider] === true)
      })
      .catch(() => {
        /* settings unreachable: keep the email sign-in only */
      })
    return () => {
      cancelled = true
    }
  }, [provider, offered])
  return offered && live
}

/**
 * `confirm` turns the card into the magic-link landing step: one button that
 * redeems the link. Mail scanners (Outlook Safe Links runs the page's script)
 * open every link in a message; redeeming on page load let them spend the
 * one-time token, and with it the 6-digit code, before the person got there.
 */
export interface ConfirmSignIn {
  onContinue: () => void
  busy: boolean
  error: string | null
  onBack: () => void
}

export function LoginScreen({ confirm }: { confirm?: ConfirmSignIn } = {}) {
  const { signIn, getClient, refreshSession } = useOmniAuth()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(() => {
    const problem = confirm ? null : peekSignInNotice()
    return problem ? providerErrorMessage(problem.error, problem.description, OPEN_NAME) : null
  })
  const [msBusy, setMsBusy] = useState(false)
  const microsoft = useProviderLive('azure', OFFERS_MICROSOFT && !confirm)

  // Opened on a deep link (an alert email's lead): come back here after the
  // magic link, not to the start page. The callback's own card is skipped.
  useEffect(() => {
    if (!confirm) rememberReturnTo()
  }, [confirm])

  // A provider error handed over by the callback is shown once.
  useEffect(() => {
    if (notice) clearSignInNotice()
  }, [notice])

  async function onMicrosoft() {
    if (msBusy) return
    setMsBusy(true)
    setNotice(null)
    try {
      // PKCE (flowType is set once on the shared client for this instance):
      // the browser goes to Microsoft and comes back to /auth/callback?code=…
      const { error: oauthError } = await getClient('mail').auth.signInWithOAuth({
        provider: 'azure',
        options: { scopes: 'email', redirectTo: `${window.location.origin}/auth/callback?app=mail` },
      })
      if (oauthError) throw oauthError
    } catch (err) {
      setMsBusy(false)
      setNotice(providerErrorMessage('start_failed', err instanceof Error ? err.message : null, OPEN_NAME))
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed || status === 'sending') return
    setStatus('sending')
    setError(null)
    try {
      await signIn(trimmed)
      setStatus('sent')
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Sign-in failed. Try again.')
    }
  }

  async function onVerifyCode(e: FormEvent) {
    e.preventDefault()
    const token = code.replace(/\D/g, '')
    if (token.length < 6 || status === 'verifying') return
    setStatus('verifying')
    setError(null)
    try {
      const client = getClient('mail')
      const { error: verifyError } = await client.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token,
        type: 'email',
      })
      if (verifyError) throw verifyError
      await refreshSession('mail')
      // provider state now carries the session; App switches to the chrome
    } catch (err) {
      setStatus('sent')
      setError(
        err instanceof Error && err.message.toLowerCase().includes('expired')
          ? 'That code has expired. Request a new one.'
          : 'That code did not match. Check the newest email.',
      )
    }
  }

  const sent = status === 'sent' || status === 'verifying'
  const field =
    'w-full rounded-lg border border-slate-300 bg-white px-3 text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/25'

  const card = (
    <section className="rounded-xl bg-white p-6 text-slate-900 shadow-2xl shadow-black/30 sm:p-7">
      {confirm ? (
        <div className="flex flex-col gap-3">
          <div className="mb-2">
            <h2 className="text-xl font-extrabold">You're nearly in</h2>
            <p className="mt-1 text-sm text-slate-600">Tap the button to open {OPEN_NAME}.</p>
          </div>
          <Button
            type="button"
            size="lg"
            autoFocus
            loading={confirm.busy}
            onClick={confirm.onContinue}
            className="mt-1 w-full font-bold"
            style={entry.buttonText ? { color: entry.buttonText } : undefined}
          >
            Continue
          </Button>
          {confirm.error && (
            <>
              <p className="text-sm text-red-600">{confirm.error}</p>
              <button type="button" className="text-xs text-slate-500 hover:text-slate-900" onClick={confirm.onBack}>
                Request a new link
              </button>
            </>
          )}
        </div>
      ) : sent ? (
        <form onSubmit={onVerifyCode} className="flex flex-col gap-3">
          <div className="mb-1 text-center">
            <MailCheck className="mx-auto h-9 w-9" style={{ color: theme.accent }} aria-hidden />
            <h2 className="mt-3 text-lg font-extrabold">Check your email</h2>
            {entry.codeOnly ? (
              <p className="mt-1 text-sm text-slate-600">
                We sent a <span className="font-semibold text-slate-900">6-digit code</span> to{' '}
                <span className="font-semibold text-slate-900">{email}</span>. Type it here.
              </p>
            ) : (
              <p className="mt-1 text-sm text-slate-600">
                We sent a link and a <span className="font-semibold text-slate-900">6-digit code</span> to{' '}
                <span className="font-semibold text-slate-900">{email}</span>. Tap the link, or type the code here.
              </p>
            )}
          </div>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            aria-label="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            className={`${field} py-3 text-center text-2xl tracking-[0.4em]`}
          />
          <Button type="submit" size="lg" loading={status === 'verifying'} className="w-full">
            Sign in with code
          </Button>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="button"
            className="text-xs text-slate-500 hover:text-slate-900"
            onClick={() => {
              setStatus('idle')
              setCode('')
              setError(null)
            }}
          >
            Use a different email
          </button>
        </form>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <div className="mb-2">
            <h2 className="text-xl font-extrabold">Sign in</h2>
            <p className="mt-1 text-sm text-slate-600">
              {microsoft
                ? 'Use your Microsoft work account, or we email you a one-tap link.'
                : (entry.emailHint ??
                  (entry.codeOnly ? 'We email you a 6-digit code. No password.' : 'We email you a one-tap sign-in link. No password.'))}
            </p>
          </div>
          {notice && (
            <p
              role="alert"
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-snug text-amber-900"
            >
              {notice}
            </p>
          )}
          {microsoft && (
            <>
              <button
                type="button"
                onClick={onMicrosoft}
                disabled={msBusy}
                className="flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 text-[15px] font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/25 disabled:cursor-wait disabled:opacity-70"
              >
                <MicrosoftMark />
                {msBusy ? 'Opening Microsoft…' : 'Sign in with Microsoft'}
              </button>
              <div className="my-1 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <span className="h-px flex-1 bg-slate-200" />
                or use your email
                <span className="h-px flex-1 bg-slate-200" />
              </div>
            </>
          )}
          <label htmlFor="login-email" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Work email
          </label>
          <input
            id="login-email"
            type="email"
            required
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={entry.emailPlaceholder ?? 'you@company.com'}
            className={`${field} py-2.5 text-sm`}
          />
          <Button
            type="submit"
            size="lg"
            loading={status === 'sending'}
            className="mt-1 w-full font-bold"
            style={entry.buttonText ? { color: entry.buttonText } : undefined}
          >
            {entry.codeOnly ? 'Email me a code' : 'Send magic link'}
          </Button>
          {status === 'error' && error && <p className="text-sm text-red-600">{error}</p>}
        </form>
      )}
    </section>
  )
  const access = (
    <p className="mt-6 text-center text-sm text-white/70">
      Need access? Ask your {ADMIN_NAME} administrator.
    </p>
  )

  if (entry.room) return <LoginRoom card={card} room={entry.room} />
  if (COVER) return <Cover card={card} access={access} />

  return (
    <main
      className="relative flex min-h-dvh w-full items-center justify-center overflow-x-hidden overflow-y-auto px-4 py-10 sm:py-14"
      style={{ background: GROUND }}
    >
      {/* Depth: a soft glow of the brand highlight, a faint grid, a vignette. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute left-1/2 top-[-18rem] h-[36rem] w-[56rem] -translate-x-1/2 rounded-full blur-3xl"
          style={{ background: GLOW }}
        />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 50% 30%, transparent 35%, rgba(0,0,0,0.35) 100%)' }}
        />
      </div>

      <div className="relative w-full max-w-[400px]">
        <header className="mb-8 flex flex-col items-center gap-5 text-center">
          <Logo />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: HIGHLIGHT }}>
              {instance.displayName}
            </p>
            <h1 className="mt-2 text-[1.75rem] font-extrabold uppercase leading-none tracking-[0.08em] text-white sm:text-[2.1rem]">
              {TITLE}
            </h1>
            {entry.tagline && (
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/65 sm:text-xs">
                {entry.tagline}
              </p>
            )}
            <div aria-hidden className="mx-auto mt-5 h-[3px] w-12 rounded-full" style={{ background: HIGHLIGHT }} />
          </div>
        </header>

        {card}

        {access}
      </div>
    </main>
  )
}

/**
 * The cover layout (instance.entry.cover; Nateva first, Sam 16 Oct 2026: the
 * sign-in should open like the NZ Funds Client Intelligence cover). The
 * product named large on the left, the same sign-in card on the right, a real
 * photo of the customer's people behind the card side on wide screens.
 */
function Cover({ card, access }: { card: ReactNode; access: ReactNode }) {
  const c = COVER!
  const shade = alpha(theme.accent, 1)
  return (
    <main className="relative min-h-dvh w-full overflow-x-hidden overflow-y-auto" style={{ background: GROUND }}>
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {c.photoUrl && (
          <div
            className="absolute inset-y-0 right-0 hidden w-[62%] lg:block"
            style={{
              maskImage: 'linear-gradient(90deg, transparent 0%, #000 38%)',
              WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 38%)',
            }}
          >
            <img src={c.photoUrl} alt="" className="h-full w-full object-cover" />
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(90deg, ${shade} 0%, ${alpha(theme.accent, 0.6)} 45%, ${alpha(theme.accent, 0.45)} 100%)`,
              }}
            />
          </div>
        )}
        <div
          className="absolute right-[-12rem] top-[-14rem] h-[34rem] w-[48rem] rounded-full blur-3xl"
          style={{ background: GLOW }}
        />
      </div>

      <div className="relative mx-auto grid min-h-dvh w-full max-w-[1280px] grid-cols-1 content-center gap-10 px-6 py-12 sm:px-10 lg:grid-cols-[minmax(0,1fr)_400px] lg:items-center lg:gap-16 lg:px-16">
        <header className="text-white">
          {c.eyebrow && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/70 sm:text-xs">{c.eyebrow}</p>
          )}
          <div className="mt-6 sm:mt-8">
            {LOGO ? (
              <img
                src={LOGO}
                alt={instance.displayName}
                style={{ height: LOGO_HEIGHT, maxHeight: '16vw', width: 'auto', maxWidth: '100%' }}
                className="object-contain"
              />
            ) : (
              <span className="text-5xl font-semibold">{instance.displayName}</span>
            )}
          </div>
          <div aria-hidden className="mt-6 h-1 w-40 rounded-full sm:mt-8" style={{ background: HIGHLIGHT }} />
          <h1
            className="mt-5 font-semibold leading-[0.95] tracking-[-0.035em] sm:mt-6"
            style={{ fontSize: 'clamp(52px, 8.4vw, 120px)', color: HIGHLIGHT }}
          >
            {c.title}
          </h1>
          {c.line && <p className="mt-5 max-w-[34rem] text-lg leading-relaxed text-white/85 sm:mt-6 sm:text-xl">{c.line}</p>}
        </header>

        <div className="w-full max-w-[400px] lg:justify-self-end">
          {card}
          {access}
        </div>
      </div>

      <p className="absolute bottom-5 left-6 right-6 text-xs text-white/55 sm:left-10 lg:left-16">{c.footer ?? productName}</p>
    </main>
  )
}

/** Microsoft's four-square mark, in its own colours (their sign-in button guidance). */
function MicrosoftMark() {
  return (
    <svg aria-hidden width="20" height="20" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}

/** The real logo, never redrawn: straight on the ground, or on a plate. */
function Logo() {
  if (!LOGO) {
    // No artwork on file: the name's initial on a glass tile.
    return (
      <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-4xl font-extrabold text-white shadow-lg backdrop-blur">
        {instance.displayName.charAt(0)}
      </div>
    )
  }
  const img: CSSProperties = { height: LOGO_HEIGHT, maxHeight: '22vw', width: 'auto', maxWidth: '100%' }
  const tag = <img src={LOGO} alt={instance.displayName} style={img} className="object-contain" />
  if (!entry.logoPlate) return <div className="flex max-w-full justify-center">{tag}</div>
  return (
    <div
      className="flex max-w-full justify-center rounded-2xl px-7 py-5 shadow-xl shadow-black/25 sm:px-9 sm:py-6"
      style={{ background: entry.logoPlate }}
    >
      {tag}
    </div>
  )
}
